import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import AnalyticsBrief from 'components/AnalyticsBrief';
import { WhiteBox, WhiteBoxHeader } from 'components/AppShell';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    width: '100%',
  },
  scrollContainer: {
    display: 'flex',
    overflowX: 'auto',
    gap: theme.spacing(4),
    padding: theme.spacing(8, 0.5),
    scrollBehavior: 'smooth',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: theme.palette.shades['clr-white-900'],
    border: `1px solid ${theme.palette.neutral['clr-200']}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      backgroundColor: theme.palette.primary['clr-50'],
      borderColor: theme.palette.primary['clr-200'],
    },
  },
  navButtonLeft: {
    left: theme.spacing(1),
  },
  navButtonRight: {
    right: theme.spacing(1),
  },
  cardContainer: {
    minWidth: '216px',
    flexShrink: 0,
  },
  arrowIcon: {
    fontSize: '14px',
    color: '#666',
  },
}));

// Moved outside component to avoid recreation on each render
const scoreData = [
  {
    title: "Interview Average",
    data: 0,
    percentage: 0,
    increment: true,
    subTitle: "Your overall interview performance across all case studies",
    info: "Reflects changes in your overall interview average after the last attempt",
    showComparision: false
  },
  {
    title: "Case Average",
    data: 4.75,
    percentage: 38,
    increment: true,
    subTitle: "Your case analysis and problem-solving performance",
    info: "Reflects changes in your case study average after the last attempt",
    showComparision: true,
    attempts: 3
  },
  {
    title: "Core concepts Average",
    data: 3.25,
    percentage: 38,
    increment: true,
    subTitle: "Your understanding of business fundamentals and frameworks",
    info: "Reflects changes in your core concepts average after the last attempt",
    showComparision: true,
    attempts: 3
  },
  {
    title: "Critical Thinking Average",
    data: 1.56,
    percentage: 38,
    increment: true,
    subTitle: "Your analytical and logical reasoning performance",
    info: "Reflects changes in your critical thinking average after the last attempt",
    showComparision: true,
    attempts: 3
  }
];

const ScoreCard = () => {
  const classes = useStyles();
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  const handleScrollLeft = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -250, behavior: 'smooth' });
    }
  }, []);

  const handleScrollRight = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 250, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial check

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  return (
    <WhiteBox>
      <WhiteBoxHeader
        heading="Score card"
        subheading="Cumulative average scores of your personalized interviews"
      />
      
      <Box className={classes.container} mt={4}>
        {showLeftArrow && (
          <Box
            className={`${classes.navButton} ${classes.navButtonLeft}`}
            onClick={handleScrollLeft}
          >
            <ArrowBackIosIcon className={classes.arrowIcon} />
          </Box>
        )}

        {showRightArrow && (
          <Box
            className={`${classes.navButton} ${classes.navButtonRight}`}
            onClick={handleScrollRight}
          >
            <ArrowForwardIosIcon className={classes.arrowIcon} />
          </Box>
        )}
        
        <Box
          ref={scrollRef}
          className={classes.scrollContainer}
        >
          {scoreData.map((score, index) => (
            <Box key={index} className={classes.cardContainer}>
              <AnalyticsBrief
                title={score.title}
                data={score.data}
                percentage={score.percentage}
                increment={score.increment}
                subTitle={score.subTitle}
                info={score.info}
                showComparision={score.showComparision}
                attempts={score.attempts || 1}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </WhiteBox>
  );
};

export default ScoreCard;
